import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Briefcase, Building2, MapPin, Save, Plus, Trash2, Edit3,
  Phone, Globe, Mail, Image as ImageIcon, Upload, X, Clock,
  Users, ChevronDown, ChevronUp, AlertCircle,
} from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import Header from '../components/Header';
import MainTabsNav from '../components/MainTabsNav';
import Footer from '../components/Footer';
import AdminSidebar from '../components/AdminSidebar';
import AddressSearch from '../components/AddressSearch';
import RichTextEditor from '../components/RichTextEditor';
import JobApplicantsList from '../components/JobApplicantsList';
import { POSITION_OPTIONS, isKnownPosition, getPositionLabel } from '../utils/jobPositions';

interface EmployerProfile {
  id: string;
  provider_id: string;
  company_photos: string[];
  address: string | null;
  address_detail: string | null;
  latitude: number | null;
  longitude: number | null;
  description: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  is_premium_employer: boolean;
}

interface JobPosting {
  id: string;
  title: string;
  position: string;
  employment_type: string;
  description: string;
  salary_type: string | null;
  salary_min: number | null;
  salary_max: number | null;
  work_location: string | null;
  work_location_detail: string | null;
  work_location_lat: number | null;
  work_location_lng: number | null;
  work_hours: string | null;
  work_days: string | null;
  requirements: string | null;
  preferred_skills: string | null;
  required_certificates: string[];
  recruitment_count: number | null;
  deadline: string | null;
  status: string;
  contact_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  created_at: string;
  updated_at: string;
}

const EMPLOYMENT_TYPES = [
  { value: 'FULL_TIME', label: '정규직' },
  { value: 'CONTRACT', label: '계약직' },
  { value: 'PART_TIME', label: '파트타임' },
  { value: 'FREELANCE', label: '프리랜서' },
];

// Postgres INTEGER 컬럼(salary_min/salary_max/recruitment_count) 범위
const MAX_SALARY_VALUE = 2147483647;

const SALARY_TYPES = [
  { value: 'HOURLY', label: '시급' },
  { value: 'DAILY', label: '일급' },
  { value: 'MONTHLY', label: '월급' },
  { value: 'YEARLY', label: '연봉' },
  { value: 'NEGOTIABLE', label: '협의' },
];

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  OPEN: { label: '모집중', color: 'bg-green-100 text-green-700' },
  CLOSED: { label: '마감', color: 'bg-gray-100 text-gray-700' },
  FILLED: { label: '채용완료', color: 'bg-blue-100 text-blue-700' },
  DRAFT: { label: '임시저장', color: 'bg-yellow-100 text-yellow-700' },
  CANCELLED: { label: '취소', color: 'bg-red-100 text-red-700' },
};

export default function ProviderRecruitmentPage() {
  const { user, isAuthenticated, checkSession } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'company-info' | 'job-postings'>(
    searchParams.get('tab') === 'job-postings' ? 'job-postings' : 'company-info'
  );
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Company info state
  const [companyInfo, setCompanyInfo] = useState({
    address: '',
    addressDetail: '',
    description: '',
    phone: '',
    email: '',
    website: '',
  });
  const [companyPhotos, setCompanyPhotos] = useState<string[]>([]);
  const [savingInfo, setSavingInfo] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileCoords, setProfileCoords] = useState<{ lat: number; lng: number } | null>(null);

  // Job postings state
  const [jobPostings, setJobPostings] = useState<JobPosting[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [showJobForm, setShowJobForm] = useState(false);
  const [editingJob, setEditingJob] = useState<JobPosting | null>(null);
  const [savingJob, setSavingJob] = useState(false);
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);

  // Job form state
  const [jobForm, setJobForm] = useState({
    title: '',
    position: '',
    employmentType: 'FULL_TIME',
    description: '',
    salaryType: 'MONTHLY',
    salaryMin: '',
    salaryMax: '',
    workLocation: '',
    workHours: '',
    workDays: '',
    requirements: '',
    preferredSkills: '',
    recruitmentCount: '',
    deadline: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
  });
  const [phonePrefix, setPhonePrefix] = useState('010');
  // 폼에서 미리보기용 좌표 (저장 후/편집 시 채워짐)
  const [jobFormCoords, setJobFormCoords] = useState<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    checkSession();
  }, [isAuthenticated, navigate, checkSession]);

  // Load employer profile
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoadingProfile(true);
        const response = await fetch('/api/v1/provider/employer-profile', {
          credentials: 'include',
        });
        if (response.ok) {
          const result = await response.json();
          if (result.data) {
            const p = result.data;
            setCompanyInfo({
              address: p.address || '',
              addressDetail: p.address_detail || '',
              description: p.description || '',
              phone: p.phone || '',
              email: p.email || '',
              website: p.website || '',
            });
            setCompanyPhotos(p.company_photos || []);
            if (p.latitude && p.longitude) {
              setProfileCoords({ lat: p.latitude, lng: p.longitude });
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch employer profile:', err);
      } finally {
        setLoadingProfile(false);
      }
    };
    if (user) fetchProfile();
  }, [user]);

  // Load job postings
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoadingJobs(true);
        const response = await fetch('/api/v1/provider/jobs', {
          credentials: 'include',
        });
        if (response.ok) {
          const result = await response.json();
          setJobPostings(result.data || []);
        }
      } catch (err) {
        console.error('Failed to fetch job postings:', err);
      } finally {
        setLoadingJobs(false);
      }
    };
    if (user) fetchJobs();
  }, [user]);

  if (!isAuthenticated) return null;

  // --- Company Info handlers ---
  const handleSaveCompanyInfo = async () => {
    setSavingInfo(true);
    setMessage(null);
    try {
      const response = await fetch('/api/v1/provider/employer-profile', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...companyInfo,
          companyPhotos,
        }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);
      if (result.data?.latitude && result.data?.longitude) {
        setProfileCoords({ lat: result.data.latitude, lng: result.data.longitude });
      }
      setMessage({ type: 'success', text: '업체 정보가 저장되었습니다.' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || '저장에 실패했습니다.' });
    } finally {
      setSavingInfo(false);
    }
  };

  const handleUploadPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: '이미지 파일만 업로드할 수 있습니다.' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: '이미지 크기는 5MB 이하여야 합니다.' });
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('index', String(companyPhotos.length));

      const response = await fetch('/api/v1/provider/employer-profile/photos', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);
      setCompanyPhotos(prev => [...prev, result.data.url]);
      setMessage({ type: 'success', text: '사진이 업로드되었습니다.' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || '업로드에 실패했습니다.' });
    }
    e.target.value = '';
  };

  const handleRemovePhoto = (index: number) => {
    setCompanyPhotos(prev => prev.filter((_, i) => i !== index));
  };

  // --- Job Posting handlers ---
  const buildEmployerAddressString = () => {
    const a = companyInfo.address || '';
    const d = companyInfo.addressDetail || '';
    if (!a) return '';
    return d ? `${a}, ${d}` : a;
  };

  // 신규 등록: 업체 주소를 디폴트로 (구인구직 포지션별 서브페이지에서 진입 시 position 프리셋)
  const openNewJobForm = (initialPosition = '') => {
    setJobForm({
      title: '', position: initialPosition, employmentType: 'FULL_TIME', description: '',
      salaryType: 'MONTHLY', salaryMin: '', salaryMax: '',
      workLocation: buildEmployerAddressString(),
      workHours: '', workDays: '',
      requirements: '', preferredSkills: '', recruitmentCount: '', deadline: '',
      contactName: '', contactPhone: '', contactEmail: '',
    });
    setPhonePrefix('010');
    setJobFormCoords(profileCoords);
    setEditingJob(null);
    setShowJobForm(true);
  };

  // 구인구직 페이지 "새 구인 글 등록하기" 버튼(?tab=job-postings&new=1, 포지션별 서브페이지에서는 &position=... 추가)으로 진입 시
  // 업체 프로필 로딩이 끝난 뒤(디폴트 주소를 채우기 위해) 자동으로 등록 폼을 연다.
  useEffect(() => {
    if (loadingProfile || searchParams.get('new') !== '1') return;
    setActiveTab('job-postings');
    openNewJobForm(searchParams.get('position') || '');
    // 구인구직 서브페이지 등에서 스크롤이 내려간 채로 넘어올 수 있어 폼이 보이도록 최상단으로 이동
    window.scrollTo(0, 0);
    const next = new URLSearchParams(searchParams);
    next.delete('new');
    next.delete('position');
    setSearchParams(next, { replace: true });
  }, [loadingProfile]);

  const resetJobForm = () => {
    setJobForm({
      title: '', position: '', employmentType: 'FULL_TIME', description: '',
      salaryType: 'MONTHLY', salaryMin: '', salaryMax: '',
      workLocation: '', workHours: '', workDays: '',
      requirements: '', preferredSkills: '', recruitmentCount: '', deadline: '',
      contactName: '', contactPhone: '', contactEmail: '',
    });
    setPhonePrefix('010');
    setJobFormCoords(null);
    setEditingJob(null);
    setShowJobForm(false);
  };

  const handleEditJob = (job: JobPosting) => {
    setEditingJob(job);
    const wl = job.work_location || buildEmployerAddressString();
    const wlDetail = job.work_location_detail || '';
    const merged = wlDetail && !wl.includes(wlDetail) ? `${wl}, ${wlDetail}` : wl;
    setJobForm({
      title: job.title,
      position: job.position,
      employmentType: job.employment_type,
      description: job.description,
      salaryType: job.salary_type || 'MONTHLY',
      salaryMin: job.salary_min?.toString() || '',
      salaryMax: job.salary_max?.toString() || '',
      workLocation: merged,
      workHours: job.work_hours || '',
      workDays: job.work_days || '',
      requirements: job.requirements || '',
      preferredSkills: job.preferred_skills || '',
      recruitmentCount: job.recruitment_count?.toString() || '',
      deadline: job.deadline || '',
      contactName: job.contact_name || '',
      contactPhone: (() => {
        const p = job.contact_phone || '';
        const PREFIXES = ['010', '02', '031', '032', '033', '041', '042', '043', '044', '051', '052', '053', '054', '055', '061', '062', '063', '064', '070'];
        const found = PREFIXES.find(px => p.startsWith(px + '-') || p.startsWith(px));
        return found ? p.slice(found.length).replace(/^-/, '') : p;
      })(),
      contactEmail: job.contact_email || '',
    });
    setPhonePrefix((() => {
      const p = job.contact_phone || '';
      const PREFIXES = ['010', '02', '031', '032', '033', '041', '042', '043', '044', '051', '052', '053', '054', '055', '061', '062', '063', '064', '070'];
      return PREFIXES.find(px => p.startsWith(px + '-') || p.startsWith(px)) || '010';
    })());
    if (job.work_location_lat && job.work_location_lng) {
      setJobFormCoords({ lat: job.work_location_lat, lng: job.work_location_lng });
    } else if (!job.work_location && profileCoords) {
      // 기존 공고에 work_location이 없으면 업체 좌표로 fallback
      setJobFormCoords(profileCoords);
    } else {
      setJobFormCoords(null);
    }
    setShowJobForm(true);
  };

  // 주소가 새로 선택되면 좌표는 저장 시까지 미상으로 표시
  const handleJobAddressChange = (newAddress: string) => {
    setJobForm(prev => ({ ...prev, workLocation: newAddress }));
    setJobFormCoords(null);
  };

  const handleSaveJob = async () => {
    if (!jobForm.title || !jobForm.position || !jobForm.employmentType || !jobForm.description || !jobForm.deadline) {
      setMessage({ type: 'error', text: '제목, 포지션, 고용형태, 모집 마감일, 설명은 필수입니다.' });
      return;
    }
    if (jobForm.contactEmail && !jobForm.contactEmail.includes('@')) {
      setMessage({ type: 'error', text: '이메일 형식이 올바르지 않습니다. (@를 포함해주세요)' });
      return;
    }
    const oversizedField = [
      { label: '최소 급여', value: jobForm.salaryMin },
      { label: '최대 급여', value: jobForm.salaryMax },
      { label: '모집 인원', value: jobForm.recruitmentCount },
    ].find(f => f.value !== '' && Number(f.value) > MAX_SALARY_VALUE);
    if (oversizedField) {
      setMessage({ type: 'error', text: `${oversizedField.label} 값이 너무 큽니다. (${MAX_SALARY_VALUE.toLocaleString()} 이하로 입력해주세요)` });
      return;
    }

    const contactPhone = jobForm.contactPhone
      ? `${phonePrefix}-${jobForm.contactPhone}`
      : '';

    setSavingJob(true);
    setMessage(null);
    try {
      const url = editingJob
        ? `/api/v1/provider/jobs/${editingJob.id}`
        : '/api/v1/provider/jobs';

      const response = await fetch(url, {
        method: editingJob ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...jobForm, contactPhone }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);

      // 응답에서 새 좌표 받아 미리보기에 반영 (저장 직후 form은 닫지만, 다음 편집 진입 시 좌표가 정확)
      const saved = result.data;
      if (saved?.work_location_lat && saved?.work_location_lng) {
        setJobFormCoords({ lat: saved.work_location_lat, lng: saved.work_location_lng });
      }

      // Refresh list
      const listResponse = await fetch('/api/v1/provider/jobs', { credentials: 'include' });
      const listResult = await listResponse.json();
      setJobPostings(listResult.data || []);

      resetJobForm();
      setMessage({ type: 'success', text: editingJob ? '구인 정보가 수정되었습니다.' : '구인 정보가 등록되었습니다.' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || '저장에 실패했습니다.' });
    } finally {
      setSavingJob(false);
    }
  };

  const handleDeleteJob = async (id: string) => {
    if (!confirm('정말로 이 구인 정보를 삭제하시겠습니까?')) return;
    try {
      const response = await fetch(`/api/v1/provider/jobs/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) throw new Error('삭제에 실패했습니다.');
      setJobPostings(prev => prev.filter(j => j.id !== id));
      setMessage({ type: 'success', text: '구인 정보가 삭제되었습니다.' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const handleCloseJob = async (id: string) => {
    if (!confirm('이 구인 정보를 마감하시겠습니까?')) return;
    try {
      const response = await fetch(`/api/v1/provider/jobs/${id}/close`, {
        method: 'PUT',
        credentials: 'include',
      });
      if (!response.ok) throw new Error('마감에 실패했습니다.');
      const result = await response.json();
      setJobPostings(prev => prev.map(j => j.id === id ? result.data : j));
      setMessage({ type: 'success', text: '구인 정보가 마감되었습니다.' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <MainTabsNav className="mt-16" />
      <div className="flex flex-1 max-w-[1440px] mx-auto w-full">
        <AdminSidebar />

        <main className="flex-1 p-4">
          <div>
            {/* 헤더 */}
            <div className="mb-4">
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Briefcase className="w-6 h-6 text-primary" />
                채용 관리
              </h1>
              <p className="text-gray-500 mt-1">
                업체 정보 및 구인 정보를 관리하세요.
              </p>
            </div>

            {/* 메시지 */}
            {message && (
              <div
                className={`mb-4 p-3 rounded-lg ${
                  message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                }`}
              >
                {message.text}
              </div>
            )}

            {/* 탭 메뉴 */}
            <div className="mb-6 border-b border-gray-200">
              <nav className="flex space-x-8">
                <button
                  onClick={() => setActiveTab('company-info')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'company-info'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    업체 정보
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('job-postings')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'job-postings'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    구인 정보 설정
                  </div>
                </button>
              </nav>
            </div>

            {/* 업체 정보 탭 */}
            {activeTab === 'company-info' && (
              <div className="space-y-6">
                {/* 회사 사진 */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <ImageIcon className="w-5 h-5" />
                    업체 사진
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    {companyPhotos.map((url, index) => (
                      <div key={index} className="relative group aspect-video rounded-lg overflow-hidden border border-gray-200">
                        <img src={url} alt={`회사 사진 ${index + 1}`} className="w-full h-full object-cover" />
                        <button
                          onClick={() => handleRemovePhoto(index)}
                          className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    {companyPhotos.length < 8 && (
                      <label className="aspect-video rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors">
                        <Upload className="w-6 h-6 text-gray-400 mb-1" />
                        <span className="text-xs text-gray-500">사진 추가</span>
                        <input type="file" accept="image/*" className="hidden" onChange={handleUploadPhoto} />
                      </label>
                    )}
                  </div>
                  <p className="text-xs text-gray-400">최대 8장, 5MB 이하 이미지 파일</p>
                </div>

                {/* 주소 + 지도 미리보기 */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    업체 주소
                  </h3>
                  <AddressSearch
                    value={companyInfo.address}
                    onChange={(addr) => setCompanyInfo(prev => ({ ...prev, address: addr }))}
                    required
                  />
                  {/* Google Maps 미리보기 */}
                  {companyInfo.address && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">위치 미리보기</p>
                      {profileCoords ? (
                        <iframe
                          width="100%"
                          height="300"
                          style={{ border: 0 }}
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          src={`https://www.google.com/maps/embed/v1/place?key=${(window as any).__GOOGLE_MAPS_API_KEY || 'AIzaSyCoR825_R9dtOug9dolmdrXmvKdSHplvg0'}&q=${profileCoords.lat},${profileCoords.lng}&zoom=15&language=ko`}
                          className="rounded-lg"
                        />
                      ) : (
                        <div className="bg-gray-100 rounded-lg h-[300px] flex items-center justify-center text-gray-500 text-sm">
                          <div className="text-center">
                            <MapPin className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                            저장하면 지도가 표시됩니다
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* 업체 추가 정보 */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">업체 추가 정보</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                        <Phone className="w-4 h-4 inline mr-1" />연락처
                      </label>
                      <input
                        type="text"
                        value={companyInfo.phone}
                        onChange={e => setCompanyInfo(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="02-1234-5678"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                        <Mail className="w-4 h-4 inline mr-1" />이메일
                      </label>
                      <input
                        type="email"
                        value={companyInfo.email}
                        onChange={e => setCompanyInfo(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="info@company.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                        <Globe className="w-4 h-4 inline mr-1" />웹사이트
                      </label>
                      <input
                        type="url"
                        value={companyInfo.website}
                        onChange={e => setCompanyInfo(prev => ({ ...prev, website: e.target.value }))}
                        placeholder="https://www.company.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-semibold text-gray-900 mb-1.5">업체 소개</label>
                    <textarea
                      value={companyInfo.description}
                      onChange={e => setCompanyInfo(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="업체에 대한 간단한 소개를 작성해주세요."
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                    />
                  </div>
                </div>

                {/* 저장 버튼 */}
                <div className="flex justify-end">
                  <button
                    onClick={handleSaveCompanyInfo}
                    disabled={savingInfo}
                    className="flex items-center gap-2 px-6 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary-600 transition-colors disabled:opacity-50"
                  >
                    <Save className="w-4 h-4" />
                    {savingInfo ? '저장 중...' : '업체 정보 저장'}
                  </button>
                </div>
              </div>
            )}

            {/* 구인 정보 설정 탭 */}
            {activeTab === 'job-postings' && (
              <div className="space-y-4">
                {/* 새 구인 정보 추가 버튼 */}
                {!showJobForm && (
                  <button
                    onClick={() => openNewJobForm()}
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-600 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    새 구인 정보 등록
                  </button>
                )}

                {/* 구인 정보 입력 폼 */}
                {showJobForm && (
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      {editingJob ? '구인 정보 수정' : '새 구인 정보 등록'}
                    </h3>

                    <div className="space-y-4">
                      {/* 기본 정보 */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                            공고 제목 <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={jobForm.title}
                            onChange={e => setJobForm(prev => ({ ...prev, title: e.target.value }))}
                            placeholder="예: 필라테스 강사 모집"
                            maxLength={200}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                            모집 포지션 <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={isKnownPosition(jobForm.position) ? jobForm.position : (jobForm.position ? '__OTHER__' : '')}
                            onChange={e => {
                              const v = e.target.value;
                              if (v === '__OTHER__') return; // 기존 자유 텍스트 유지 (편집 시 호환)
                              setJobForm(prev => ({ ...prev, position: v }));
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm bg-white"
                          >
                            <option value="" disabled>포지션을 선택하세요</option>
                            {POSITION_OPTIONS.map(p => (
                              <option key={p.value} value={p.value}>{p.label}</option>
                            ))}
                            {jobForm.position && !isKnownPosition(jobForm.position) && (
                              <option value="__OTHER__">{`기존: ${jobForm.position}`}</option>
                            )}
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                            고용 형태 <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={jobForm.employmentType}
                            onChange={e => setJobForm(prev => ({ ...prev, employmentType: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                          >
                            {EMPLOYMENT_TYPES.map(t => (
                              <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-900 mb-1.5">모집 인원</label>
                          <input
                            type="number"
                            value={jobForm.recruitmentCount}
                            onChange={e => setJobForm(prev => ({ ...prev, recruitmentCount: e.target.value }))}
                            placeholder="0"
                            min="0"
                            max={MAX_SALARY_VALUE}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                          />
                        </div>
                      </div>

                      {/* 급여 */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-900 mb-1.5">급여 형태</label>
                          <select
                            value={jobForm.salaryType}
                            onChange={e => setJobForm(prev => ({ ...prev, salaryType: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                          >
                            {SALARY_TYPES.map(t => (
                              <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                          </select>
                        </div>
                        {jobForm.salaryType !== 'NEGOTIABLE' && (
                          <>
                            <div>
                              <label className="block text-sm font-semibold text-gray-900 mb-1.5">최소 급여 (원)</label>
                              <input
                                type="number"
                                value={jobForm.salaryMin}
                                onChange={e => setJobForm(prev => ({ ...prev, salaryMin: e.target.value }))}
                                placeholder="0"
                                min="0"
                                max={MAX_SALARY_VALUE}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-semibold text-gray-900 mb-1.5">최대 급여 (원)</label>
                              <input
                                type="number"
                                value={jobForm.salaryMax}
                                onChange={e => setJobForm(prev => ({ ...prev, salaryMax: e.target.value }))}
                                placeholder="0"
                                min="0"
                                max={MAX_SALARY_VALUE}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                              />
                            </div>
                          </>
                        )}
                      </div>

                      {/* 근무 조건 */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                            <Clock className="w-4 h-4 inline mr-1" />근무 시간
                          </label>
                          <input
                            type="text"
                            value={jobForm.workHours}
                            onChange={e => setJobForm(prev => ({ ...prev, workHours: e.target.value }))}
                            placeholder="예: 09:00 ~ 18:00"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-900 mb-1.5">근무 요일</label>
                          <input
                            type="text"
                            value={jobForm.workDays}
                            onChange={e => setJobForm(prev => ({ ...prev, workDays: e.target.value }))}
                            placeholder="예: 월~금"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                          />
                        </div>
                      </div>

                      {/* 근무지 주소 (업체 주소가 디폴트, 호점별로 다르면 수정) */}
                      <div className="rounded-lg border border-gray-200 p-4 bg-gray-50/40">
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-sm font-semibold text-gray-900">
                            <MapPin className="w-4 h-4 inline mr-1" />
                            근무지 주소
                          </label>
                          {companyInfo.address && jobForm.workLocation !== buildEmployerAddressString() && (
                            <button
                              type="button"
                              onClick={() => {
                                const def = buildEmployerAddressString();
                                setJobForm(prev => ({ ...prev, workLocation: def }));
                                setJobFormCoords(profileCoords);
                              }}
                              className="text-xs text-primary hover:underline"
                            >
                              업체 주소로 되돌리기
                            </button>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mb-2">
                          업체 주소가 기본으로 설정됩니다. 호점이 다른 경우 주소를 변경해주세요.
                        </p>
                        <AddressSearch
                          value={jobForm.workLocation}
                          onChange={handleJobAddressChange}
                        />
                        {/* Google Maps 미리보기 */}
                        {jobForm.workLocation && (
                          <div className="mt-3">
                            <p className="text-xs font-medium text-gray-700 mb-1.5">위치 미리보기</p>
                            {jobFormCoords ? (
                              <iframe
                                width="100%"
                                height="240"
                                style={{ border: 0 }}
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                src={`https://www.google.com/maps/embed/v1/place?key=${(window as any).__GOOGLE_MAPS_API_KEY || 'AIzaSyCoR825_R9dtOug9dolmdrXmvKdSHplvg0'}&q=${jobFormCoords.lat},${jobFormCoords.lng}&zoom=15&language=ko`}
                                className="rounded-lg"
                              />
                            ) : (
                              <div className="bg-gray-100 rounded-lg h-[180px] flex items-center justify-center text-gray-500 text-xs">
                                <div className="text-center">
                                  <MapPin className="w-7 h-7 mx-auto mb-1.5 text-gray-400" />
                                  저장하면 지도가 표시됩니다
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-1.5">모집 마감일 <span className="text-red-500">*</span></label>
                        <input
                          type="date"
                          value={jobForm.deadline}
                          onChange={e => setJobForm(prev => ({ ...prev, deadline: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                          required
                        />
                      </div>

                      {/* 상세 설명 */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                          상세 설명 <span className="text-red-500">*</span>
                        </label>
                        <RichTextEditor
                          content={jobForm.description}
                          onChange={(html) => setJobForm(prev => ({ ...prev, description: html }))}
                          placeholder="직무 내용, 근무 환경, 복리후생 등을 상세히 작성해주세요."
                          minHeight="200px"
                        />
                      </div>

                      {/* 자격 요건 */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-1.5">자격 요건</label>
                        <textarea
                          value={jobForm.requirements}
                          onChange={e => setJobForm(prev => ({ ...prev, requirements: e.target.value }))}
                          placeholder="필수 자격 요건을 작성해주세요."
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-1.5">우대 사항</label>
                        <textarea
                          value={jobForm.preferredSkills}
                          onChange={e => setJobForm(prev => ({ ...prev, preferredSkills: e.target.value }))}
                          placeholder="우대 사항을 작성해주세요."
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                        />
                      </div>

                      {/* 담당자 연락처 */}
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-3">담당자 연락처</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">담당자명</label>
                            <input
                              type="text"
                              value={jobForm.contactName}
                              onChange={e => setJobForm(prev => ({ ...prev, contactName: e.target.value }))}
                              maxLength={100}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">연락처</label>
                            <div className="flex gap-1">
                              <select
                                value={phonePrefix}
                                onChange={e => setPhonePrefix(e.target.value)}
                                className="px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm bg-white"
                              >
                                <option value="010">010</option>
                                <option value="02">02</option>
                                <option value="031">031</option>
                                <option value="032">032</option>
                                <option value="033">033</option>
                                <option value="041">041</option>
                                <option value="042">042</option>
                                <option value="043">043</option>
                                <option value="044">044</option>
                                <option value="051">051</option>
                                <option value="052">052</option>
                                <option value="053">053</option>
                                <option value="054">054</option>
                                <option value="055">055</option>
                                <option value="061">061</option>
                                <option value="062">062</option>
                                <option value="063">063</option>
                                <option value="064">064</option>
                                <option value="070">070</option>
                              </select>
                              <input
                                type="text"
                                value={jobForm.contactPhone}
                                onChange={e => setJobForm(prev => ({ ...prev, contactPhone: e.target.value.replace(/[^0-9-]/g, '') }))}
                                placeholder="0000-0000"
                                maxLength={20}
                                className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">이메일</label>
                            <input
                              type="email"
                              value={jobForm.contactEmail}
                              onChange={e => setJobForm(prev => ({ ...prev, contactEmail: e.target.value }))}
                              placeholder="example@email.com"
                              maxLength={100}
                              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm ${
                                jobForm.contactEmail && !jobForm.contactEmail.includes('@')
                                  ? 'border-red-400 bg-red-50'
                                  : 'border-gray-300'
                              }`}
                            />
                            {jobForm.contactEmail && !jobForm.contactEmail.includes('@') && (
                              <p className="text-xs text-red-500 mt-1">@ 기호를 포함해주세요</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* 폼 버튼 */}
                      <div className="flex justify-end gap-2 pt-4 border-t">
                        <button
                          onClick={resetJobForm}
                          className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                        >
                          취소
                        </button>
                        <button
                          onClick={handleSaveJob}
                          disabled={savingJob}
                          className="flex items-center gap-2 px-6 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 text-sm"
                        >
                          <Save className="w-4 h-4" />
                          {savingJob ? '저장 중...' : editingJob ? '수정' : '등록'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* 구인 정보 목록 */}
                {loadingJobs ? (
                  <div className="text-center py-8 text-gray-500">로딩 중...</div>
                ) : jobPostings.length === 0 && !showJobForm ? (
                  <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                    <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">등록된 구인 정보가 없습니다.</p>
                    <p className="text-sm text-gray-400 mt-1">새 구인 정보를 등록해보세요.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {jobPostings.map(job => {
                      const statusInfo = STATUS_LABELS[job.status] || STATUS_LABELS.OPEN;
                      const isExpanded = expandedJobId === job.id;
                      return (
                        <div key={job.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                          {/* 요약 행 */}
                          <div
                            className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                            onClick={() => setExpandedJobId(isExpanded ? null : job.id)}
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                                {statusInfo.label}
                              </span>
                              <div className="min-w-0">
                                <h4 className="font-semibold text-gray-900 truncate">{job.title}</h4>
                                <p className="text-sm text-gray-500">
                                  {getPositionLabel(job.position)} · {EMPLOYMENT_TYPES.find(t => t.value === job.employment_type)?.label}
                                  {job.deadline && ` · 마감: ${job.deadline}`}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                              {job.status === 'OPEN' && (
                                <>
                                  <button
                                    onClick={e => { e.stopPropagation(); handleEditJob(job); }}
                                    className="p-1.5 text-gray-400 hover:text-primary rounded-lg hover:bg-gray-100"
                                    title="수정"
                                  >
                                    <Edit3 className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={e => { e.stopPropagation(); handleCloseJob(job.id); }}
                                    className="p-1.5 text-gray-400 hover:text-yellow-600 rounded-lg hover:bg-gray-100"
                                    title="마감"
                                  >
                                    <AlertCircle className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                              <button
                                onClick={e => { e.stopPropagation(); handleDeleteJob(job.id); }}
                                className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-gray-100"
                                title="삭제"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                              {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                            </div>
                          </div>

                          {/* 상세 정보 */}
                          {isExpanded && (
                            <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
                                {job.salary_type && (
                                  <div>
                                    <span className="text-gray-500">급여</span>
                                    <p className="font-medium">
                                      {SALARY_TYPES.find(t => t.value === job.salary_type)?.label}
                                      {job.salary_min || job.salary_max ? ` ${(job.salary_min || 0).toLocaleString()}~${(job.salary_max || 0).toLocaleString()}원` : ''}
                                    </p>
                                  </div>
                                )}
                                {job.work_hours && (
                                  <div>
                                    <span className="text-gray-500">근무 시간</span>
                                    <p className="font-medium">{job.work_hours}</p>
                                  </div>
                                )}
                                {job.recruitment_count && (
                                  <div>
                                    <span className="text-gray-500">모집 인원</span>
                                    <p className="font-medium">{job.recruitment_count}명</p>
                                  </div>
                                )}
                              </div>
                              {job.description && (
                                <div className="mt-2">
                                  <span className="text-sm text-gray-500">상세 설명</span>
                                  <div className="mt-1 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: job.description }} />
                                </div>
                              )}
                              {job.requirements && (
                                <div className="mt-3">
                                  <span className="text-sm text-gray-500">자격 요건</span>
                                  <p className="mt-1 text-sm whitespace-pre-wrap">{job.requirements}</p>
                                </div>
                              )}
                              {/* 근무지 주소 + 지도 */}
                              {job.work_location && (
                                <div className="mt-3 text-sm">
                                  <span className="text-gray-500 flex items-center gap-1">
                                    <MapPin className="w-3.5 h-3.5" /> 근무지
                                  </span>
                                  <p className="font-medium mt-0.5">{job.work_location}</p>
                                  {job.work_location_lat && job.work_location_lng && (
                                    <iframe
                                      width="100%"
                                      height="200"
                                      style={{ border: 0 }}
                                      loading="lazy"
                                      referrerPolicy="no-referrer-when-downgrade"
                                      src={`https://www.google.com/maps/embed/v1/place?key=${(window as any).__GOOGLE_MAPS_API_KEY || 'AIzaSyCoR825_R9dtOug9dolmdrXmvKdSHplvg0'}&q=${job.work_location_lat},${job.work_location_lng}&zoom=15&language=ko`}
                                      className="rounded-lg mt-2"
                                    />
                                  )}
                                </div>
                              )}

                              {job.contact_name && (
                                <div className="mt-3 text-sm">
                                  <span className="text-gray-500">담당자</span>
                                  <p className="font-medium">
                                    {job.contact_name}
                                    {job.contact_phone && ` · ${job.contact_phone}`}
                                    {job.contact_email && ` · ${job.contact_email}`}
                                  </p>
                                </div>
                              )}

                              {/* 지원자 목록 */}
                              <div className="mt-4 pt-4 border-t border-gray-100">
                                <JobApplicantsList jobPostingId={job.id} />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
